import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useProcessTemplates, useProcessTemplatePreview, useApplyProcessTemplate } from '../../process-templates';
import type { EstimateItem } from '../../shared/types/estimate.types';

export function ProcessApplyDrawer({
  item,
  estimateId,
  onClose,
}: {
  item: EstimateItem;
  estimateId: string;
  onClose: () => void;
}) {
  const { templates, isLoading } = useProcessTemplates(item.company_id, { category_code: item.category_code, status_code: 'active' });
  const [templateId, setTemplateId] = useState('');
  const selectedTemplate = templates.find((template) => template.id === templateId) || null;
  const { preview, isLoading: isPreviewLoading } = useProcessTemplatePreview(templateId, Number(item.quantity || 0));
  const [selectedStepIds, setSelectedStepIds] = useState<string[]>([]);
  const { applyProcessTemplate, isApplying } = useApplyProcessTemplate(estimateId);

  useEffect(() => {
    setSelectedStepIds(preview?.steps.map((step) => step.step.id) || []);
  }, [preview]);

  const visibleSteps = useMemo(() => {
    if (!preview) return [];
    const selected = new Set(selectedStepIds);
    return preview.steps.filter((step) => selected.has(step.step.id));
  }, [preview, selectedStepIds]);

  const totalAmount = visibleSteps.reduce((sum, step) => sum + step.total_amount, 0);

  const toggleStep = (stepId: string) => {
    setSelectedStepIds((current) => current.includes(stepId) ? current.filter((id) => id !== stepId) : [...current, stepId]);
  };

  const apply = async () => {
    if (!selectedTemplate) return;
    await applyProcessTemplate({ templateId: selectedTemplate.id, itemId: item.id, selectedStepIds });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/30">
      <aside className="ml-auto flex h-full w-full max-w-3xl flex-col bg-white shadow-sm">
        <div className="shrink-0 border-b border-slate-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="sm:text-sm text-xs font-medium text-slate-900">Áp quy trình vào dự toán</h2>
              <p className="mt-1 sm:text-sm text-xs text-slate-500">{item.item_code} - {item.item_name}</p>
            </div>
            <Button size="icon" variant="ghost" onClick={onClose}><i className="ri-close-line" /></Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_180px]">
            <select value={templateId} onChange={(event) => setTemplateId(event.target.value)} className="field">
              <option value="">Chọn quy trình mẫu phù hợp</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.template_code} - {template.template_name}</option>
              ))}
            </select>
            <div className="rounded-md border border-slate-100 bg-slate-50 p-2 sm:text-sm text-xs text-slate-600">
              KL gốc: <span className="font-medium text-slate-900">{(item.quantity || 0).toLocaleString('vi-VN')} {item.unit}</span>
            </div>
          </div>

          {isLoading || isPreviewLoading ? (
            <div className="sm:text-sm text-xs text-slate-500">Đang tải quy trình...</div>
          ) : templates.length === 0 ? (
            <EmptyState title="Chưa có quy trình phù hợp" description="Hãy tạo quy trình mẫu cho nhóm hạng mục này trước khi áp." />
          ) : !preview ? (
            <EmptyState title="Chọn quy trình để xem trước" description="Bản xem trước sẽ hiển thị các bước, tài nguyên và tổng tiền dự kiến." />
          ) : (
            <div className="space-y-3">
              {preview.steps.map((step) => {
                const checked = selectedStepIds.includes(step.step.id);
                return (
                  <div key={step.step.id} className="rounded-md border border-slate-200 bg-white p-3">
                    <label className="flex items-start gap-3">
                      <input type="checkbox" checked={checked} onChange={() => toggleStep(step.step.id)} className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="info">{step.step.step_code}</Badge>
                          <Badge variant="neutral">{step.quantity.toLocaleString('vi-VN')} {step.step.unit}</Badge>
                          <Badge variant="neutral">{step.resources.length} tài nguyên</Badge>
                          {step.resources.some((resource) => resource.price_warning) && <Badge variant="warning">Thiếu giá</Badge>}
                        </div>
                        <p className="mt-2 sm:text-sm text-xs font-medium text-slate-900">{step.step.step_name}</p>
                        <p className="mt-1 sm:text-sm text-xs text-slate-500">Tạm tính: {step.total_amount.toLocaleString('vi-VN')} đ</p>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="sm:text-sm text-xs text-slate-600">Tổng tạm tính: <span className="font-medium text-slate-900">{totalAmount.toLocaleString('vi-VN')} đ</span></p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Hủy</Button>
              <Button disabled={!selectedTemplate || visibleSteps.length === 0} isLoading={isApplying} onClick={apply}>Áp vào dự toán</Button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
